import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import AdminShell from "../AdminShell";

const API = process.env.REACT_APP_API_URL || "";

const styles = {
  container: { maxWidth: 820 },
  header: { marginBottom: 24 },
  title: { margin: 0, fontSize: 20, fontWeight: 700, color: "#111827" },
  subtitle: { margin: "4px 0 0", color: "#6b7280", fontSize: 13 },
  card: {
    background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb",
    padding: "28px 32px", marginBottom: 24,
  },
  sectionTitle: { margin: "0 0 18px", fontSize: 15, fontWeight: 600, color: "#111827" },
  row: { display: "flex", gap: 16, marginBottom: 16 },
  col: { flex: 1, display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 12, fontWeight: 600, color: "#374151", textTransform: "uppercase", letterSpacing: "0.04em" },
  input: {
    padding: "9px 12px", borderRadius: 8, border: "1px solid #d1d5db",
    fontSize: 14, color: "#111827", outline: "none",
    fontFamily: "inherit",
  },
  textarea: {
    padding: "9px 12px", borderRadius: 8, border: "1px solid #d1d5db",
    fontSize: 14, color: "#111827", outline: "none", resize: "vertical",
    fontFamily: "inherit", minHeight: 80,
  },
  radioGroup: { display: "flex", gap: 20, marginBottom: 16 },
  radioLabel: { display: "flex", alignItems: "center", gap: 6, fontSize: 14, color: "#374151", cursor: "pointer" },
  sendBtn: {
    padding: "10px 28px", background: "#1a3c5e", color: "#fff",
    border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600,
    cursor: "pointer", marginTop: 4,
  },
  sendBtnDisabled: {
    padding: "10px 28px", background: "#9ca3af", color: "#fff",
    border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600,
    cursor: "not-allowed", marginTop: 4,
  },
  successBanner: {
    background: "#ecfdf5", border: "1px solid #6ee7b7", borderRadius: 8,
    padding: "10px 16px", color: "#065f46", fontSize: 13, marginBottom: 16,
  },
  errorBanner: {
    background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8,
    padding: "10px 16px", color: "#991b1b", fontSize: 13, marginBottom: 16,
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    textAlign: "left", padding: "8px 12px", background: "#f9fafb",
    borderBottom: "1px solid #e5e7eb", color: "#6b7280", fontWeight: 600,
    fontSize: 11, textTransform: "uppercase",
  },
  td: { padding: "10px 12px", borderBottom: "1px solid #f3f4f6", color: "#374151" },
  badge: (type) => ({
    display: "inline-block", padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600,
    background: type === "all" ? "#eff6ff" : "#f0fdf4",
    color: type === "all" ? "#1d4ed8" : "#166534",
  }),
  emptyRow: { textAlign: "center", padding: "32px 12px", color: "#9ca3af", fontSize: 13 },
};

const AdminNotifications = () => {
  const { auth } = useSelector((state) => state);
  const token = auth?.data?.accesstoken || auth?.data?.access_token;

  // Form state
  const [audience, setAudience] = useState("all");
  const [titleEn, setTitleEn] = useState("");
  const [messageEn, setMessageEn] = useState("");
  const [titleAr, setTitleAr] = useState("");
  const [messageAr, setMessageAr] = useState("");

  // UI state
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  // Broadcasts list
  const [broadcasts, setBroadcasts] = useState([]);
  const [loadingBroadcasts, setLoadingBroadcasts] = useState(true);

  const fetchBroadcasts = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/admin/notifications/broadcasts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.status === 1) setBroadcasts(res.data.broadcasts || []);
    } catch (e) {
      console.error("Failed to load broadcasts:", e.message);
    } finally {
      setLoadingBroadcasts(false);
    }
  }, [token]);

  useEffect(() => { fetchBroadcasts(); }, [fetchBroadcasts]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!titleEn.trim() || !messageEn.trim()) {
      setError("English title and message are required.");
      return;
    }
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        title_en: titleEn.trim(),
        message_en: messageEn.trim(),
        title_ar: titleAr.trim() || titleEn.trim(),
        message_ar: messageAr.trim() || messageEn.trim(),
      };
      // For 'all' audience, omit userIds — backend sends to all users
      const res = await axios.post(`${API}/api/admin/notifications/broadcast`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.status === 1) {
        const { targetCount, pushStats } = res.data;
        setSuccess(
          `Broadcast sent to ${res.data.targetCount ?? "all"} users. ` +
          (pushStats ? `Push: ${pushStats.sent} delivered, ${pushStats.failed} failed.` : "")
        );
        setTitleEn(""); setMessageEn(""); setTitleAr(""); setMessageAr("");
        fetchBroadcasts();
      } else {
        setError(res.data.msg || "Failed to send broadcast.");
      }
    } catch (err) {
      setError(err.response?.data?.msg || err.message || "Network error.");
    } finally {
      setSending(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-SA", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  return (
    <AdminShell pageTitle="Push Notifications">
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>Push Notifications</h2>
          <p style={styles.subtitle}>Send bilingual push notifications to all users or specific segments.</p>
        </div>

        {/* ── Send Form ── */}
        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>Send New Notification</h3>

          {success && <div style={styles.successBanner}>✅ {success}</div>}
          {error && <div style={styles.errorBanner}>⚠️ {error}</div>}

          <form onSubmit={handleSend}>
            {/* Audience selector — reserved for future specific-user targeting */}
            <div style={{ marginBottom: 16 }}>
              <div style={styles.label}>Target Audience</div>
              <div style={styles.radioGroup}>
                <label style={styles.radioLabel}>
                  <input type="radio" value="all" checked={audience === "all"} onChange={() => setAudience("all")} />
                  All Users
                </label>
                {/* Specific users selector — future enhancement */}
              </div>
            </div>

            {/* English content */}
            <div style={{ marginBottom: 4 }}>
              <div style={styles.label}>English Content</div>
            </div>
            <div style={styles.row}>
              <div style={styles.col}>
                <label style={styles.label}>Title (EN)</label>
                <input
                  style={styles.input}
                  placeholder="e.g. New Property Available"
                  value={titleEn}
                  onChange={e => setTitleEn(e.target.value)}
                  required
                />
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={styles.label}>Message (EN)</label>
              <textarea
                style={styles.textarea}
                placeholder="Notification body in English..."
                value={messageEn}
                onChange={e => setMessageEn(e.target.value)}
                required
              />
            </div>

            {/* Arabic content */}
            <div style={{ marginBottom: 4 }}>
              <div style={styles.label}>Arabic Content <span style={{ color: "#9ca3af", fontWeight: 400, textTransform: "none" }}>(optional — falls back to English)</span></div>
            </div>
            <div style={styles.row}>
              <div style={styles.col}>
                <label style={styles.label}>Title (AR)</label>
                <input
                  style={{ ...styles.input, direction: "rtl", textAlign: "right" }}
                  placeholder="مثال: عقار جديد متاح"
                  value={titleAr}
                  onChange={e => setTitleAr(e.target.value)}
                />
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={styles.label}>Message (AR)</label>
              <textarea
                style={{ ...styles.textarea, direction: "rtl", textAlign: "right" }}
                placeholder="نص الإشعار بالعربية..."
                value={messageAr}
                onChange={e => setMessageAr(e.target.value)}
              />
            </div>

            <button
              type="submit"
              style={sending ? styles.sendBtnDisabled : styles.sendBtn}
              disabled={sending}
            >
              {sending ? "Sending…" : "🔔 Send Notification"}
            </button>
          </form>
        </div>

        {/* ── Recent Broadcasts ── */}
        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>Recent Broadcasts</h3>
          {loadingBroadcasts ? (
            <p style={{ color: "#9ca3af", fontSize: 13 }}>Loading…</p>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Title (EN)</th>
                  <th style={styles.th}>Audience</th>
                  <th style={styles.th}>Recipients</th>
                  <th style={styles.th}>Push Sent</th>
                  <th style={styles.th}>Sent At</th>
                </tr>
              </thead>
              <tbody>
                {broadcasts.length === 0 ? (
                  <tr><td colSpan={5} style={styles.emptyRow}>No broadcasts sent yet.</td></tr>
                ) : (
                  broadcasts.map(b => (
                    <tr key={b._id}>
                      <td style={styles.td}>
                        <div style={{ fontWeight: 600 }}>{b.title_en}</div>
                        <div style={{ color: "#9ca3af", fontSize: 12, marginTop: 2 }}>{b.message_en?.slice(0, 60)}{b.message_en?.length > 60 ? "…" : ""}</div>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.badge(b.targetType)}>{b.targetType === "all" ? "All Users" : "Specific"}</span>
                      </td>
                      <td style={styles.td}>{b.targetCount ?? "—"}</td>
                      <td style={styles.td}>
                        {b.pushSent ?? 0}
                        {b.pushFailed > 0 && <span style={{ color: "#ef4444", marginLeft: 4 }}>({b.pushFailed} failed)</span>}
                      </td>
                      <td style={styles.td}>{formatDate(b.createdAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminShell>
  );
};

export default AdminNotifications;
